import { getState } from '../../app/store'

/**
 * Retro sound effects, synthesised with WebAudio: a short blip when the snake
 * eats (its own for a chili and for the pineapple) and a noise burst when it
 * dies. Silent unless sound is on (footer "play"); "mute" silences them
 * together with the ambient sound.
 *
 * Browsers only let a page start audio from a user gesture, so the
 * AudioContext is created — or resumed — by unlock(), which the game calls
 * from its key, tap and "play" handlers.
 */
const BLIP_VOLUME = 0.06
const CRASH_VOLUME = 0.22
const CRASH_SECONDS = 0.45

// An ingredient gets the full blip, a square of the word (a quarter point)
// a shorter, higher and quieter tick. A chili ("hot") zips up quickly, the
// pineapple ("sick") slides down with a queasy wobble. `from` → `to` Hz over
// the `slide` share of `seconds`.
const BLIPS = {
  ingredient: {
    wave: 'square',
    from: 660,
    to: 1320,
    slide: 0.55,
    seconds: 0.09,
    volume: BLIP_VOLUME,
  },
  word: {
    wave: 'square',
    from: 990,
    to: 1980,
    slide: 0.55,
    seconds: 0.045,
    volume: BLIP_VOLUME * 0.7,
  },
  hot: {
    wave: 'sawtooth',
    from: 300,
    to: 1800,
    slide: 0.8,
    seconds: 0.18,
    volume: BLIP_VOLUME * 0.8,
  },
  sick: {
    wave: 'square',
    from: 560,
    to: 120,
    slide: 0.9,
    seconds: 0.45,
    volume: BLIP_VOLUME,
    wobble: { rate: 11, depth: 35 },
  },
}

export function createSounds() {
  let context = null

  const ready = () => getState().soundOn && context?.state === 'running'

  return {
    unlock() {
      if (!getState().soundOn) return
      const AudioContextClass = window.AudioContext ?? window.webkitAudioContext
      if (!AudioContextClass) return
      try {
        context ??= new AudioContextClass()
        if (context.state === 'suspended') context.resume().catch(() => {})
      } catch {
        context = null
      }
    },

    /** @param {keyof BLIPS} kind  the food that was eaten, or the effect it started */
    blip(kind = 'ingredient') {
      if (!ready()) return
      const { wave, from, to, slide, seconds, volume, wobble } = BLIPS[kind] ?? BLIPS.ingredient
      const now = context.currentTime
      const end = now + seconds + 0.01
      const oscillator = context.createOscillator()
      const gain = context.createGain()

      oscillator.type = wave
      oscillator.frequency.setValueAtTime(from, now)
      oscillator.frequency.exponentialRampToValueAtTime(to, now + seconds * slide)
      gain.gain.setValueAtTime(0.0001, now)
      gain.gain.exponentialRampToValueAtTime(volume, now + 0.005)
      gain.gain.exponentialRampToValueAtTime(0.0001, now + seconds)

      // A slow vibrato on top of the pitch.
      if (wobble) {
        const lfo = context.createOscillator()
        const depth = context.createGain()
        lfo.frequency.setValueAtTime(wobble.rate, now)
        depth.gain.setValueAtTime(wobble.depth, now)
        lfo.connect(depth).connect(oscillator.frequency)
        lfo.start(now)
        lfo.stop(end)
      }

      oscillator.connect(gain).connect(context.destination)
      oscillator.start(now)
      oscillator.stop(end)
    },

    crash() {
      if (!ready()) return
      const now = context.currentTime
      const length = Math.ceil(context.sampleRate * CRASH_SECONDS)
      const buffer = context.createBuffer(1, length, context.sampleRate)
      const samples = buffer.getChannelData(0)
      for (let i = 0; i < length; i++) samples[i] = Math.random() * 2 - 1

      const source = context.createBufferSource()
      const filter = context.createBiquadFilter()
      const gain = context.createGain()

      source.buffer = buffer
      filter.type = 'lowpass'
      filter.frequency.setValueAtTime(4000, now)
      filter.frequency.exponentialRampToValueAtTime(180, now + CRASH_SECONDS)
      gain.gain.setValueAtTime(CRASH_VOLUME, now)
      gain.gain.exponentialRampToValueAtTime(0.0001, now + CRASH_SECONDS)

      source.connect(filter).connect(gain).connect(context.destination)
      source.start(now)
      source.stop(now + CRASH_SECONDS)
    },

    close() {
      context?.close().catch(() => {})
      context = null
    },
  }
}
